/**
 * PageForm — Wrapper visual de formularios.
 *
 * v1.9.1 — Rediseñado como slide-over lateral moderno (drawer) siguiendo la
 * request del cliente: "no me gustan las pantallas emergentes, quiero algo
 * más moderno y no tradicional".
 *
 * Se abre desde la derecha con animación smooth, mantiene el contexto visible
 * atrás bajo un backdrop con blur sutil. Estilo Odoo/Linear/Notion.
 *
 * API compatible con la versión previa — todos los llamados existentes siguen
 * funcionando (title, breadcrumb, subtitle, onBack, headerRight, children).
 *
 * Props extra opcionales:
 *   - width: ancho del drawer (default 780px)
 *   - accent: color primario (default #0A3747)
 *   - accentSecondary: color secundario (default #E0E621 lima)
 *   - footerActions: acciones sticky al pie (opcional)
 *   - variant: "drawer" (default, nuevo) o "fullscreen" (legacy)
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, X } from "lucide-react";

export default function PageForm({
  title,
  subtitle,
  breadcrumb,
  onBack,
  headerRight,
  children,
  maxWidth,          // legacy prop — se ignora en variant="drawer"
  width = "780px",   // nuevo — ancho del drawer
  testId,
  variant = "drawer", // "drawer" | "fullscreen"
  accent = "#0A3747",
  accentSecondary = "#E0E621",
  footerActions,
}) {
  const [mounted, setMounted] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 10);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
    // eslint-disable-next-line
  }, []);

  const handleClose = () => {
    if (closing) return;
    setClosing(true);
    setMounted(false);
    setTimeout(() => onBack?.(), 260);
  };

  // ═══ Variant: FULLSCREEN (legacy — para casos que lo necesiten) ═══
  if (variant === "fullscreen") {
    const content = (
      <div className="fixed inset-0 z-[10000] bg-white flex flex-col" data-testid={testId || "page-form"}>
        <div className="border-b border-gray-200 bg-gradient-to-r from-[#0A3747] to-[#0f4a5f] text-white flex-shrink-0">
          <div className={`${maxWidth || "max-w-4xl"} mx-auto px-6 py-4 flex items-center gap-4`}>
            <button onClick={handleClose} className="flex items-center gap-2 px-3 py-2 -ml-3 rounded-md hover:bg-white/10 text-sm font-medium transition text-white" data-testid="page-form-back">
              <ArrowLeft size={16} /> Regresar
            </button>
            <div className="w-px h-8 bg-white/20" />
            <div className="flex-1 min-w-0">
              {breadcrumb && (
                <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#E0E621]/80 mb-0.5 truncate">{breadcrumb}</div>
              )}
              <h1 className="text-lg font-bold truncate text-white" style={{ fontFamily: "Space Grotesk" }}>{title}</h1>
              {subtitle && <div className="text-xs text-white/70 mt-0.5 truncate">{subtitle}</div>}
            </div>
            {headerRight}
            <button onClick={handleClose} className="p-2 rounded-md hover:bg-white/10 text-white transition" aria-label="Cerrar" data-testid="page-form-close">
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto bg-gray-50">
          <div className={`${maxWidth || "max-w-4xl"} mx-auto px-6 py-6 pb-16`}>{children}</div>
        </div>
      </div>
    );
    return createPortal(content, document.body);
  }

  // ═══ Variant: DRAWER (default — nuevo · slide-over moderno) ═══
  const content = (
    <div className="fixed inset-0 z-[10000] flex justify-end" data-testid={testId || "page-form"}>
      {/* Backdrop translúcido con blur */}
      <button
        aria-label="Cerrar"
        onClick={handleClose}
        className={`absolute inset-0 bg-slate-900/30 backdrop-blur-[3px] transition-opacity duration-300 cursor-default ${
          mounted && !closing ? "opacity-100" : "opacity-0"
        }`}
        data-testid="page-form-backdrop"
      />
      {/* Panel drawer */}
      <div
        className={`relative h-full bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] ${
          mounted && !closing ? "translate-x-0" : "translate-x-full"
        }`}
        style={{
          width: "100%",
          maxWidth: width,
          borderLeft: `4px solid ${accentSecondary}`,
        }}
        data-testid="page-form-drawer"
      >
        {/* Header */}
        <div
          className="flex-shrink-0 border-b border-slate-200/70 px-5 py-3.5 relative overflow-hidden"
          style={{ backgroundImage: `linear-gradient(135deg, ${accent} 0%, ${accent}dd 100%)` }}
        >
          {/* Textura sutil */}
          <div
            className="absolute inset-0 opacity-[0.06] pointer-events-none"
            style={{ backgroundImage: `radial-gradient(circle at 20% 50%, ${accentSecondary} 0%, transparent 45%)` }}
          />
          <div className="relative flex items-start gap-3">
            <button
              onClick={handleClose}
              className="flex-shrink-0 mt-0.5 p-1.5 rounded-md hover:bg-white/10 text-white/90 hover:text-white transition-all group"
              aria-label="Regresar"
              data-testid="page-form-back"
            >
              <ArrowLeft size={16} className="group-hover:-translate-x-0.5 transition-transform" />
            </button>
            <div className="flex-1 min-w-0">
              {breadcrumb && (
                <div
                  className="text-[9.5px] font-mono uppercase tracking-[0.22em] truncate mb-1"
                  style={{ color: accentSecondary, opacity: 0.85 }}
                >
                  {breadcrumb}
                </div>
              )}
              <h1
                className="text-[15px] font-bold text-white truncate leading-tight"
                style={{ fontFamily: "Space Grotesk, Chivo, sans-serif" }}
              >
                {title}
              </h1>
              {subtitle && <div className="text-[11px] text-white/70 mt-0.5 truncate">{subtitle}</div>}
            </div>
            {headerRight}
            <button
              onClick={handleClose}
              className="flex-shrink-0 p-1.5 rounded-md hover:bg-white/10 text-white/80 hover:text-white transition-all -mr-1"
              aria-label="Cerrar"
              data-testid="page-form-close"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Body scrollable */}
        <div className="flex-1 overflow-y-auto bg-slate-50/40" data-testid="page-form-body">
          <div className="px-5 py-4">{children}</div>
        </div>

        {/* Footer sticky opcional */}
        {footerActions && (
          <div
            className="flex-shrink-0 border-t border-slate-200 bg-white px-5 py-3 flex items-center gap-2 shadow-[0_-4px_16px_rgba(0,0,0,0.04)]"
            data-testid="page-form-footer"
          >
            {footerActions}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
